import { ReactNode, useEffect, useState } from 'react'
import { AppWindow, Building2, KeyRound, Users } from 'lucide-react'
import { api } from './api'
import { Link } from './navigation'
import { eventLabel, Metric, Notice, outcomeLabel, StatusPill } from './ui'
import { SystemLayout } from './SystemAdmins'
import './system-overview.css'

type Overview = {
  tenants: Array<{ id: string; slug: string; name: string; status: string }>
  clients: Array<{ id: string; tenant_id?: string; name: string; status: string }>
  people: Array<{ id: string; tenant_id: string; status: string }>
  connections: Array<{ id: string; tenant_id: string; name: string; status: string }>
  keys: Array<{ id: string; kid: string; state: string; not_after: number }>
  audit: Array<{ id: string; at: number; type: string; severity: string; outcome: string }>
}
type GoogleInventory = { setups: Array<{ connection_id: string }>; grants: Array<{ status: string }>; providers: unknown[] }
type Attention = { tone: 'critical' | 'warning'; text: string; href: string; action: string }

const request = <T,>(path: string) => api<T>(path, { signal: AbortSignal.timeout(20000) })

export function attentionItems(overview: Overview, google: GoogleInventory | null): Attention[] {
  const items: Attention[] = []
  const nowSeconds = Date.now() / 1000
  if (!overview.keys.some((key) => key.state === 'active' && key.not_after > nowSeconds)) {
    items.push({ tone: 'critical', text: 'No current signing key. Apps cannot verify new sign-ins.', href: '/system/activity', action: 'Check activity' })
  }
  const compromised = overview.keys.filter((key) => key.state === 'compromised').length
  if (compromised) items.push({ tone: 'critical', text: `${compromised} signing ${compromised === 1 ? 'key is' : 'keys are'} marked compromised.`, href: '/system/activity', action: 'Check activity' })
  const paused = overview.connections.filter((connection) => connection.status !== 'active').length
  if (paused) items.push({ tone: 'warning', text: `${paused} company sign-in ${paused === 1 ? 'connection is' : 'connections are'} not active.`, href: '/system/activity', action: 'Check activity' })
  if (google) {
    if (!google.providers.length) items.push({ tone: 'warning', text: 'Google app is not registered.', href: '/system/google-setup', action: 'Register' })
    const failing = google.grants.filter((grant) => grant.status !== 'active').length
    if (failing) items.push({ tone: 'warning', text: `${failing} Google ${failing === 1 ? 'authorization needs' : 'authorizations need'} attention.`, href: '/system/connections', action: 'Review' })
    const pending = google.setups.length - google.grants.filter((grant) => grant.status === 'active').length
    if (pending > 0) items.push({ tone: 'warning', text: `${pending} Google ${pending === 1 ? 'service is' : 'services are'} awaiting admin approval.`, href: '/system/connections', action: 'Review' })
  }
  const blocked = overview.audit.filter((event) => event.outcome === 'denied').length
  if (blocked) items.push({ tone: 'warning', text: `${blocked} blocked ${blocked === 1 ? 'action' : 'actions'} in recent activity.`, href: '/system/activity', action: 'View' })
  return items
}

// Kept as a named wrapper so System pages read consistently; navigation is the
// app's own Link (history push + navigation event), not a copy of it.
export function SystemLink({ href, children }: { href: string; children: ReactNode }) {
  return <Link to={href}>{children}</Link>
}

export function SystemOverview() {
  const [overview, setOverview] = useState<Overview | null>(null)
  const [google, setGoogle] = useState<GoogleInventory | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(true)

  const load = async () => {
    setBusy(true)
    setError('')
    const [main, googleResult] = await Promise.allSettled([
      request<Overview>('/api/v1/operator/overview'),
      request<GoogleInventory>('/api/v1/system/external-services/google'),
    ])
    if (main.status === 'fulfilled') setOverview(main.value)
    else setError('We could not load the system overview. Try again.')
    setGoogle(googleResult.status === 'fulfilled' ? googleResult.value : null)
    setBusy(false)
  }
  useEffect(() => { void load() }, [])

  const attention = overview ? attentionItems(overview, google) : []
  const peopleBy = (tenant: string) => overview?.people.filter((person) => person.tenant_id === tenant).length ?? 0
  const appsBy = (tenant: string) => overview?.clients.filter((client) => client.tenant_id === tenant).length ?? 0

  return <SystemLayout active="overview">
    <div className="page-heading">
      <div><h1>Overview</h1><p>Everything this AuthEM system serves, and what needs attention.</p></div>
      <button className="button secondary" disabled={busy} onClick={() => { void load() }}>Refresh</button>
    </div>
    {error && <><Notice tone="error">{error}</Notice><button className="button secondary" disabled={busy} onClick={() => { void load() }}>Try again</button></>}
    {busy && !overview && <p role="status">Loading overview…</p>}
    {overview && <>
      <div className="metric-grid system-metrics">
        <Metric icon={<Building2 size={18} />} label="Companies" value={overview.tenants.length} />
        <Metric icon={<Users size={18} />} label="People" value={new Set(overview.people.map((person) => person.id)).size} />
        <Metric icon={<AppWindow size={18} />} label="Apps" value={overview.clients.length} />
        <Metric icon={<KeyRound size={18} />} label="Company sign-in connections" value={overview.connections.length} />
      </div>

      <section className="section-card">
        <h2>Needs attention</h2>
        {attention.length === 0
          ? <p className="system-all-clear">Nothing needs attention.</p>
          : <ul className="system-attention">{attention.map((item) => <li key={item.text} className={item.tone}>
              <span>{item.text}</span><SystemLink href={item.href}>{item.action}</SystemLink>
            </li>)}</ul>}
        {!google && <p className="section-description">Google status could not be loaded.</p>}
      </section>

      <section className="section-card">
        <h2>Companies</h2>
        {overview.tenants.length === 0 ? <p>No companies yet.</p> : <div className="table-scroll"><table>
          <thead><tr><th>Company</th><th>Sign-in address</th><th>People</th><th>Apps</th><th>Status</th></tr></thead>
          <tbody>{overview.tenants.map((tenant) => <tr key={tenant.id}>
            <td><SystemLink href={`/system/companies/${encodeURIComponent(tenant.id)}`}>{tenant.name}</SystemLink></td>
            <td><code>/c/{tenant.slug}/login</code></td>
            <td>{peopleBy(tenant.id)}</td>
            <td>{appsBy(tenant.id)}</td>
            <td><StatusPill value={tenant.status === 'active' ? 'Active' : tenant.status} /></td>
          </tr>)}</tbody>
        </table></div>}
      </section>

      <section className="section-card">
        <div className="system-section-heading"><h2>Recent activity</h2><SystemLink href="/system/activity">View all activity</SystemLink></div>
        {overview.audit.length === 0 ? <p>No activity yet.</p> : <ul className="system-recent">{overview.audit.slice(0, 8).map((event) => <li key={event.id}>
          <span>{eventLabel(event.type)}</span>
          <span className="status-pill">{outcomeLabel(event.outcome)}</span>
          <time dateTime={new Date(event.at * 1000).toISOString()}>{new Date(event.at * 1000).toLocaleString()}</time>
        </li>)}</ul>}
      </section>
    </>}
  </SystemLayout>
}
